import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import useLocation from "../../../hooks/useLocation";
import carpet1 from "../../../assets/carpet-1.png";
import carpet2 from "../../../assets/carpet-2.png";
import logo1 from "../../../assets/logo-1.png";
import logo2 from "../../../assets/logo-2.png";
import logo3 from "../../../assets/logo-3.png";
import logo4 from "../../../assets/logo-4.png";
import logo5 from "../../../assets/logo-5.png";
import logo6 from "../../../assets/logo-6.png";

const HeroSection = () => {
  const navigate = useNavigate();
  const { categories, dealers, fetchCategories, fetchDealers } = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isResultsVisible, setIsResultsVisible] = useState(false);

  useEffect(() => {
    if (!categories || categories.length === 0) fetchCategories();
    if (!dealers || dealers.length === 0) fetchDealers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchQuery.trim().length > 1) {
      const query = searchQuery.toLowerCase();

      const filteredCategories =
        categories
          ?.filter((cat) => cat.name.toLowerCase().includes(query))
          .map((cat) => ({ ...cat, type: "category" })) || [];

      const filteredDealers =
        dealers
          ?.filter((dealer) => dealer.dealer_name.toLowerCase().includes(query))
          .map((dealer) => ({ ...dealer, type: "dealer" })) || [];

      const combinedResults = [...filteredCategories, ...filteredDealers].slice(
        0,
        8
      );

      setSearchResults(combinedResults);
      setIsResultsVisible(combinedResults.length > 0);
    } else {
      setSearchResults([]);
      setIsResultsVisible(false);
    }
  }, [searchQuery, categories, dealers]);

  const handleResultClick = (item) => {
    setIsResultsVisible(false);
    setSearchQuery("");
    if (item.type === "category") {
      navigate(`/category/${item.id}`);
    } else if (item.type === "dealer") {
      navigate(`/dealer/${item.id}`);
    }
  };

  return (
    <div className="bg-white pt-8 md:pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-8 md:gap-16">
          <div className="md:w-1/2 text-center md:text-left">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4">
              En İyi
              <br />
              Temizlikçiler
              <br />
              Yıkafoni'de!
            </h1>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl mb-8 max-w-xl mx-auto md:mx-0">
              Yıkafoni, her şehirde en iyi temizlik firmalarını bir araya
              getirerek ihtiyacınıza uygun hizmetleri kolayca bulmanızı sağlar.
            </p>

            <div className="relative max-w-md mx-auto md:mx-0">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Hizmet, kategori veya firma ara"
                  className="w-full h-14 px-6 pr-12 py-3 bg-white border border-gray-300 rounded-full text-gray-500 text-base focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent shadow-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() =>
                    searchQuery.trim().length > 1 && setIsResultsVisible(true)
                  }
                />
                <button className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                  >
                    <path
                      d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z"
                      stroke="currentColor"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              {isResultsVisible && searchResults.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  <ul>
                    {searchResults.map((item) => (
                      <li
                        key={`${item.type}-${item.id}`}
                        onClick={() => handleResultClick(item)}
                        className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <span className="font-medium text-gray-800">
                          {item.name || item.dealer_name}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">
                          ({item.type === "category" ? "Kategori" : "Bayi"})
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="md:w-1/2 mt-10 md:mt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              <div className="relative aspect-square rounded-3xl overflow-hidden order-1 sm:order-1">
                <img
                  src={carpet1}
                  alt="Temizlik Hizmeti 1"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>

              <div className="bg-[#CC349A] text-white rounded-3xl p-6 flex flex-col justify-center items-center text-center aspect-square order-2 sm:order-2">
                <div className="text-4xl sm:text-5xl lg:text-6xl font-bold">
                  +1200
                </div>
                <div className="text-lg sm:text-xl mt-2">onaylı işletme</div>
              </div>

              <div className="bg-[#4F46E5] text-white rounded-3xl p-6 flex flex-col justify-center items-center text-center aspect-square order-4 sm:order-3">
                <div className="text-4xl sm:text-5xl lg:text-6xl font-bold">
                  %94
                </div>
                <div className="text-lg sm:text-xl mt-2">
                  müşteri memnuniyeti
                </div>
              </div>

              <div className="relative aspect-square rounded-3xl overflow-hidden order-3 sm:order-4">
                <img
                  src={carpet2}
                  alt="Temizlik Hizmeti 2"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 md:mt-24">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex items-center justify-between gap-8 lg:gap-4">
            <img
              src={logo1}
              alt="Logo 1"
              className="h-8 sm:h-10 lg:h-12 object-contain mx-auto lg:mx-0"
            />
            <img
              src={logo2}
              alt="Logo 2"
              className="h-8 sm:h-10 lg:h-12 object-contain mx-auto lg:mx-0"
            />
            <img
              src={logo3}
              alt="Logo 3"
              className="h-8 sm:h-10 lg:h-12 object-contain mx-auto lg:mx-0"
            />
            <img
              src={logo4}
              alt="Logo 4"
              className="h-8 sm:h-10 lg:h-12 object-contain mx-auto lg:mx-0"
            />
            <img
              src={logo5}
              alt="Logo 5"
              className="h-8 sm:h-10 lg:h-12 object-contain mx-auto lg:mx-0"
            />
            <img
              src={logo6}
              alt="Logo 6"
              className="h-8 sm:h-10 lg:h-12 object-contain mx-auto lg:mx-0"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
